import type { SeopBands, SeopNumber, SeopTone } from '@/types/seop';

/**
 * Formatação do Balanceamento de Linha.
 *
 * Cada regra aqui é um **porte literal** do app do Herrison, e é o número que ele
 * confere de olho. Zero `toFixed`: o arredondamento do JS (half-away-from-zero em
 * cima de binário) diverge do `Intl`, e o `Intl` é o que o app original usa.
 */

const nf1 = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 });
const nf0 = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 });
const nfMoney = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const nfPct = new Intl.NumberFormat('pt-BR', {
  style: 'percent',
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

/**
 * Converte o número "de wire" de volta para número JS.
 *
 * O backend serializa `Infinity`/`NaN` como string porque JSON não os tem.
 */
export function seopNum(v: SeopNumber | null | undefined): number {
  if (typeof v === 'number') return v;
  if (v === 'Infinity') return Infinity;
  if (v === '-Infinity') return -Infinity;
  return NaN;
}

/**
 * Horas no formato `H:MM` — porte literal do `fmtHours` do original.
 *
 * `12.5` → `12:30`; `-0.5` → `-0:30`. Exibir sempre com o sufixo ` h` ao lado,
 * como o app faz.
 */
export function fmtHours(value: number): string {
  const v0 = Number(value) || 0;
  const sign = v0 < 0 ? '-' : '';
  const v = Math.abs(v0);
  const mins = Math.round(v * 60);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${sign}${h}:${String(m).padStart(2, '0')}`;
}

/**
 * Lê horas no formato do app — porte literal do `parseHours`.
 *
 * Aceita `10:30` **e** `10,5` (e `10.5`), com sinal. É o par de :func:`fmtHours`: os
 * campos de hora da tela de Capacidade mostram e recebem `H:MM`.
 */
export function parseHours(value: string | number): number {
  if (typeof value === 'number') return value;
  const bruto = String(value ?? '').trim();
  if (!bruto) return 0;
  const negativo = bruto.startsWith('-');
  const limpo = bruto.replace('-', '').replace(',', '.');
  const sinal = negativo ? -1 : 1;
  if (limpo.includes(':')) {
    const [h, m = '0'] = limpo.split(':');
    return sinal * ((Number(h) || 0) + (Number(m) || 0) / 60);
  }
  return sinal * (Number(limpo) || 0);
}

/**
 * Lê uma quantidade em unidades — o par de :func:`fmtUnits`.
 *
 * `fmtUnits(20754)` mostra `20.754`, e é desse texto que a pessoa copia de volta para o
 * campo do tamanho do lote. `Number('20.754')` daria **20,754**: um lote mil vezes
 * menor, que o motor aceita sem reclamar e que multiplica a carga do recurso por mil
 * (`lots = ceil(unidades/lote)`).
 *
 * O ponto só é descartado quando forma grupo de milhar pt-BR — `1.234`, `1.234.567`,
 * `20.754,5`. `20.75` continua valendo 20,75, e a vírgula segue sendo o decimal.
 */
export function parseUnits(value: string | number): number {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  const bruto = String(value ?? '').replace(/\s/g, '');
  if (!bruto) return 0;
  const ehMilharBR = /^-?\d{1,3}(\.\d{3})+(,\d+)?$/.test(bruto);
  const limpo = (ehMilharBR ? bruto.replace(/\./g, '') : bruto).replace(',', '.');
  const n = Number(limpo);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Lê dinheiro digitado em pt-BR — porte do `parseMoneyValue`.
 *
 * `R$ 1.234,56` → `1234.56`. Ponto é separador de milhar, vírgula é decimal.
 */
export function parseMoneyBRL(value: string | number): number {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  const limpo = String(value ?? '')
    .replace(/[R$\s.]/g, '')
    .replace(',', '.');
  const n = Number(limpo);
  return Number.isFinite(n) ? n : 0;
}

/** Corte 0–100 da projeção líquida — `clampPercent`, com default quando não é número. */
export function clampPercent(value: string | number, fallback = 100): number {
  const n = Number(String(value ?? '').replace(',', '.'));
  return Number.isFinite(n) ? Math.max(0, Math.min(100, n)) : fallback;
}

/** Utilização com uma casa decimal — `1.034` → `103,4%`. Não-finita vira `∞`. */
export function fmtUtil(util: number): string {
  return Number.isFinite(util) ? `${nf1.format(util * 100)}%` : '∞';
}

/** Unidades, lotes, contagens — inteiro em pt-BR. */
export function fmtUnits(value: number): string {
  return Number.isFinite(value) ? nf0.format(value) : '∞';
}

/** Dinheiro em reais, sempre com dois decimais. */
export function fmtMoneyBRL(value: number): string {
  return Number.isFinite(value) ? nfMoney.format(value) : '—';
}

/** Margem percentual (B-D11) — recebe a fração, exibe `12,3%`. */
export function fmtMarginPct(value: number): string {
  return Number.isFinite(value) ? nfPct.format(value) : '—';
}

/**
 * Faixas usadas quando o cálculo ainda não chegou. Espelha `BANDAS_PADRAO` do motor —
 * se as duas divergirem, a tela pisca um rótulo e troca por outro no primeiro render.
 */
export const BANDAS_PADRAO: SeopBands = { balanceado_min: 0.8, atencao_min: 1.0, critico_min: 1.1 };

/**
 * Semáforo — porte do `status(util)` do original, inclusive o mapa de cor.
 *
 * O mapa é contra-intuitivo de propósito e **não deve ser "corrigido"**: o app
 * pinta `Balanceado` de âmbar e `Folga` de verde, porque folga é ociosidade, não
 * vitória. O Herrison reconhece o dashboard dele pela cor.
 */
export function seopStatus(util: number, bands: SeopBands = BANDAS_PADRAO): { label: string; tone: SeopTone } {
  if (!Number.isFinite(util) || util > bands.critico_min) return { label: 'Crítico', tone: 'danger' };
  if (util > bands.atencao_min) return { label: 'Atenção', tone: 'danger' };
  if (util >= bands.balanceado_min) return { label: 'Balanceado', tone: 'warning' };
  return { label: 'Folga', tone: 'success' };
}

/**
 * Cor das **barras** do Dashboard.
 *
 * Regra própria e mais grossa que a do `seopStatus` — no original, folga fica na
 * cor padrão, não verde (`util>1.05?'bad':util>=.8?'warn':''`). As duas regras
 * divergem no app; manter as duas.
 */
export function barTone(util: number, bands: SeopBands = BANDAS_PADRAO): 'danger' | 'warning' | null {
  if (!Number.isFinite(util) || util > bands.atencao_min) return 'danger';
  if (util >= bands.balanceado_min) return 'warning';
  return null;
}

/** Classe de texto do tema para um tom do semáforo. */
export function toneTextClass(tone: SeopTone): string {
  if (tone === 'danger') return 'text-theme-danger';
  if (tone === 'warning') return 'text-theme-warning';
  return 'text-theme-success';
}

// --------------------------------------------------------------- meses do plano

/** O nome que a pessoa reconhece (`Nov/26`). Cai no rótulo e na data com backend antigo. */
export function seopNomeDoMes(m: { name?: string; label?: string; date?: string }): string {
  return m.name || m.label || m.date || '';
}

/**
 * Nome + janela de produção (`Nov/26 · 16/10 a 15/11`). É o que o seletor de período
 * mostra: "M1" não dizia a ninguém que mês era, e um plano de novembro foi parar em
 * setembro por causa disso (02/10/2026).
 */
export function seopMesComJanela(m: { name?: string; label?: string; date?: string; range?: string }): string {
  return m.range ? `${seopNomeDoMes(m)} · ${m.range}` : seopNomeDoMes(m);
}
